/**
 * EditarNegocio.js - Página pública para que negocios editen sus datos
 * Accesible via token temporal (sin login requerido)
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_API_URL || '';

// Servicios por categoria
const serviciosPorCategoria = {
  'veterinaria': ['Consulta', 'Vacunacion', 'Cirugia', 'Urgencias', 'Rayos X', 'Hospitalizacion', 'Estetica', 'Laboratorio'],
  'estilistas': ['Bano', 'Corte', 'Unas', 'Limpieza dental', 'Spa'],
  'paseadores': ['Paseo individual', 'Paseo grupal', 'Entrenamiento basico'],
  'entrenadores': ['Obediencia basica', 'Obediencia avanzada', 'Correccion de conducta', 'Socializacion'],
  'cuidadores': ['Cuidado en casa', 'Hospedaje', 'Visitas diarias']
};

const metodosPagoOpciones = ['efectivo', 'transferencia', 'tarjeta'];

export default function EditarNegocio() {
  const { token } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [expired, setExpired] = useState(false);
  const [lugar, setLugar] = useState(null);

  // Estado del formulario
  const [formData, setFormData] = useState({
    nombre: '',
    telefono: '',
    tieneWhatsapp: false,
    whatsappMismoNumero: true,
    whatsapp: '',
    horario: '',
    is24h: false,
    direccion: '',
    ciudad: '',
    estado: '',
    zonas: '',
    metodosPago: [],
    estacionamiento: {
      tipo: null,       // 'propio' | 'calle' | 'plaza' | 'sin' | null
      facilidad: null,  // 'muy_facil' | 'facil' | 'medio' | 'dificil' | null
      lugares: null     // number | null (solo si tipo === 'propio')
    },
    servicios: [],
    fotos: []
  });

  // Cargar datos del lugar
  useEffect(() => {
    async function cargarLugar() {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${API_URL}/api/directory/public/place/${token}`);
        const data = await response.json();

        if (!response.ok) {
          if (data.expired) {
            setExpired(true);
          }
          throw new Error(data.message || data.error);
        }

        setLugar(data.data);

        // Cargar datos en el formulario
        const l = data.data;
        const tieneWhatsapp = l.tieneWhatsapp || !!l.whatsapp || false;
        const whatsappMismoNumero = l.whatsapp ? false : true;

        // Manejar estacionamiento (puede ser string antiguo u objeto nuevo)
        let estacionamientoData = { tipo: null, facilidad: null, lugares: null };
        if (l.estacionamiento && typeof l.estacionamiento === 'object') {
          estacionamientoData = {
            tipo: l.estacionamiento.tipo || null,
            facilidad: l.estacionamiento.facilidad || null,
            lugares: l.estacionamiento.lugares || null
          };
        }

        setFormData({
          nombre: l.nombre || '',
          telefono: l.telefono || '',
          tieneWhatsapp,
          whatsappMismoNumero,
          whatsapp: l.whatsapp || '',
          horario: l.horario || '',
          is24h: l.is24h || false,
          direccion: l.direccion || '',
          ciudad: l.ciudad || '',
          estado: l.estado || '',
          zonas: Array.isArray(l.zonas) ? l.zonas.join(', ') : '',
          metodosPago: l.metodosPago || [],
          estacionamiento: estacionamientoData,
          servicios: l.servicios || [],
          fotos: l.fotos || []
        });

      } catch (err) {
        console.error('Error cargando lugar:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }

    if (token) {
      cargarLugar();
    }
  }, [token]);

  const serviciosDisponibles = useMemo(() => {
    if (!lugar?.categoria) return [];
    return serviciosPorCategoria[lugar.categoria.toLowerCase()] || [];
  }, [lugar?.categoria]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setSuccess(false);
  };

  const handleServicioToggle = (servicio) => {
    setFormData(prev => ({
      ...prev,
      servicios: prev.servicios.includes(servicio)
        ? prev.servicios.filter(s => s !== servicio)
        : [...prev.servicios, servicio]
    }));
    setSuccess(false);
  };

  const handleMetodoPagoToggle = (metodo) => {
    setFormData(prev => ({
      ...prev,
      metodosPago: prev.metodosPago.includes(metodo)
        ? prev.metodosPago.filter(m => m !== metodo)
        : [...prev.metodosPago, metodo]
    }));
    setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      // Preparar datos
      const whatsappValue = formData.tieneWhatsapp
        ? (formData.whatsappMismoNumero ? null : formData.whatsapp.trim() || null)
        : null;

      // Preparar estacionamiento (solo si tiene tipo seleccionado)
      const estacionamientoData = lugar?.tipo === 'establecimiento' && formData.estacionamiento.tipo
        ? {
            tipo: formData.estacionamiento.tipo,
            facilidad: formData.estacionamiento.facilidad || null,
            lugares: formData.estacionamiento.tipo === 'propio'
              ? (formData.estacionamiento.lugares || null)
              : null
          }
        : null;

      const data = {
        nombre: formData.nombre.trim(),
        telefono: formData.telefono.trim() || null,
        tieneWhatsapp: formData.tieneWhatsapp,
        whatsapp: whatsappValue,
        horario: formData.horario.trim() || null,
        is24h: formData.is24h,
        direccion: lugar?.tipo === 'establecimiento' ? (formData.direccion.trim() || null) : null,
        ciudad: lugar?.tipo === 'establecimiento' ? (formData.ciudad.trim() || null) : null,
        estado: lugar?.tipo === 'establecimiento' ? (formData.estado.trim() || null) : null,
        zonas: lugar?.tipo === 'proveedor' && formData.zonas.trim()
          ? formData.zonas.split(',').map(z => z.trim()).filter(z => z)
          : [],
        metodosPago: formData.metodosPago,
        estacionamiento: estacionamientoData,
        servicios: formData.servicios,
        fotos: formData.fotos
      };

      const response = await fetch(`${API_URL}/api/directory/public/place/${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });

      const result = await response.json();

      if (!response.ok) {
        if (result.expired) {
          setExpired(true);
        }
        throw new Error(result.message || result.error);
      }

      setSuccess(true);
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (err) {
      console.error('Error guardando:', err);
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Pantalla de carga
  if (loading) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="text-center">
          <div className="spinner-border text-primary mb-3" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="text-muted">Cargando datos del negocio...</p>
        </div>
      </div>
    );
  }

  // Liga expirada
  if (expired) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="card shadow-sm" style={{ maxWidth: '400px' }}>
          <div className="card-body text-center py-5">
            <div className="mb-4">
              <i className="bi bi-clock-history text-warning" style={{ fontSize: '4rem' }}></i>
            </div>
            <h4 className="mb-3">Liga expirada</h4>
            <p className="text-muted mb-4">
              Esta liga ha expirado. Contacta a LemmonTags para solicitar una nueva.
            </p>
            <a href="https://lemmontags.com" className="btn btn-outline-primary">
              Ir a LemmonTags
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Error al cargar
  if (error && !lugar) {
    return (
      <div className="min-vh-100 d-flex align-items-center justify-content-center bg-light">
        <div className="card shadow-sm" style={{ maxWidth: '400px' }}>
          <div className="card-body text-center py-5">
            <div className="mb-4">
              <i className="bi bi-exclamation-triangle text-danger" style={{ fontSize: '4rem' }}></i>
            </div>
            <h4 className="mb-3">Error</h4>
            <p className="text-muted mb-4">{error}</p>
            <button onClick={() => window.location.reload()} className="btn btn-outline-primary">
              Intentar de nuevo
            </button>
          </div>
        </div>
      </div>
    );
  }

  const esProveedor = lugar?.tipo === 'proveedor';

  return (
    <div className="min-vh-100 bg-light py-4">
      <div className="container" style={{ maxWidth: '600px' }}>
        {/* Header */}
        <div className="text-center mb-4">
          <h4 className="mb-1">Edita los datos de tu negocio</h4>
          <p className="text-muted small mb-0">
            {lugar?.nombre} - {lugar?.categoria}
          </p>
        </div>

        {/* Mensajes de estado */}
        {success && (
          <div className="alert alert-success d-flex align-items-center mb-4">
            <i className="bi bi-check-circle-fill me-2"></i>
            <div>Datos actualizados correctamente</div>
          </div>
        )}

        {error && lugar && (
          <div className="alert alert-danger d-flex align-items-center mb-4">
            <i className="bi bi-exclamation-triangle-fill me-2"></i>
            <div>{error}</div>
          </div>
        )}

        {/* Formulario */}
        <div className="card shadow-sm">
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              {/* Nombre */}
              <div className="mb-3">
                <label className="form-label">Nombre del negocio *</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.nombre}
                  onChange={(e) => handleChange('nombre', e.target.value)}
                  required
                  disabled={saving}
                />
              </div>

              {/* Telefono */}
              <div className="mb-3">
                <label className="form-label">Telefono</label>
                <input
                  type="tel"
                  className="form-control"
                  value={formData.telefono}
                  onChange={(e) => handleChange('telefono', e.target.value)}
                  disabled={saving}
                  placeholder="10 digitos"
                />
              </div>

              {/* WhatsApp */}
              <div className="mb-3">
                <div className="form-check mb-2">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="tieneWhatsapp"
                    checked={formData.tieneWhatsapp}
                    onChange={(e) => handleChange('tieneWhatsapp', e.target.checked)}
                    disabled={saving}
                  />
                  <label className="form-check-label" htmlFor="tieneWhatsapp">
                    Tiene WhatsApp
                  </label>
                </div>

                {formData.tieneWhatsapp && (
                  <div className="ms-4 ps-2 border-start">
                    <div className="form-check">
                      <input
                        type="radio"
                        className="form-check-input"
                        id="whatsapp-mismo"
                        name="whatsappOpcion"
                        checked={formData.whatsappMismoNumero}
                        onChange={() => handleChange('whatsappMismoNumero', true)}
                        disabled={saving}
                      />
                      <label className="form-check-label" htmlFor="whatsapp-mismo">
                        Mismo numero del telefono
                      </label>
                    </div>
                    <div className="form-check mt-1">
                      <input
                        type="radio"
                        className="form-check-input"
                        id="whatsapp-diferente"
                        name="whatsappOpcion"
                        checked={!formData.whatsappMismoNumero}
                        onChange={() => handleChange('whatsappMismoNumero', false)}
                        disabled={saving}
                      />
                      <label className="form-check-label" htmlFor="whatsapp-diferente">
                        Numero diferente
                      </label>
                    </div>
                    {!formData.whatsappMismoNumero && (
                      <input
                        type="tel"
                        className="form-control form-control-sm mt-2"
                        placeholder="Numero de WhatsApp"
                        value={formData.whatsapp}
                        onChange={(e) => handleChange('whatsapp', e.target.value)}
                        disabled={saving}
                      />
                    )}
                  </div>
                )}
              </div>

              {/* Horario */}
              <div className="mb-3">
                <label className="form-label">Horario de atencion</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.horario}
                  onChange={(e) => handleChange('horario', e.target.value)}
                  disabled={saving}
                  placeholder="Ej: Lunes a Viernes 9am-6pm"
                />
                <div className="form-check mt-2">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="is24h"
                    checked={formData.is24h}
                    onChange={(e) => handleChange('is24h', e.target.checked)}
                    disabled={saving}
                  />
                  <label className="form-check-label" htmlFor="is24h">
                    Abierto 24 horas
                  </label>
                </div>
              </div>

              {/* Direccion (solo establecimientos) */}
              {!esProveedor && (
                <>
                  <div className="mb-3">
                    <label className="form-label">Direccion</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.direccion}
                      onChange={(e) => handleChange('direccion', e.target.value)}
                      disabled={saving}
                      placeholder="Calle, numero, colonia"
                    />
                  </div>

                  <div className="row mb-3">
                    <div className="col-6">
                      <label className="form-label">Ciudad</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.ciudad}
                        onChange={(e) => handleChange('ciudad', e.target.value)}
                        disabled={saving}
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label">Estado</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.estado}
                        onChange={(e) => handleChange('estado', e.target.value)}
                        disabled={saving}
                      />
                    </div>
                  </div>

                  {/* Estacionamiento */}
                  <div className="mb-3">
                    <label className="form-label">Estacionamiento</label>

                    {/* Tipo de estacionamiento */}
                    <div className="mb-2">
                      <small className="text-muted d-block mb-1">Tipo:</small>
                      <div className="d-flex flex-wrap gap-2">
                        {[
                          { id: 'propio', label: 'Propio' },
                          { id: 'calle', label: 'En la calle' },
                          { id: 'plaza', label: 'Plaza cercana' },
                          { id: 'sin', label: 'Sin estacionamiento' }
                        ].map(opcion => (
                          <button
                            type="button"
                            key={opcion.id}
                            className={`btn btn-sm ${formData.estacionamiento.tipo === opcion.id ? 'btn-primary' : 'btn-outline-secondary'}`}
                            onClick={() => handleChange('estacionamiento', {
                              ...formData.estacionamiento,
                              tipo: opcion.id,
                              lugares: opcion.id !== 'propio' ? null : formData.estacionamiento.lugares
                            })}
                            disabled={saving}
                          >
                            {opcion.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Cantidad de lugares (solo si es propio) */}
                    {formData.estacionamiento.tipo === 'propio' && (
                      <div className="mb-2">
                        <small className="text-muted d-block mb-1">Cantidad de lugares:</small>
                        <input
                          type="number"
                          className="form-control form-control-sm"
                          style={{ width: '100px' }}
                          min="1"
                          max="100"
                          value={formData.estacionamiento.lugares || ''}
                          onChange={(e) => handleChange('estacionamiento', {
                            ...formData.estacionamiento,
                            lugares: e.target.value ? parseInt(e.target.value) : null
                          })}
                          disabled={saving}
                          placeholder="Ej: 5"
                        />
                      </div>
                    )}

                    {/* Facilidad */}
                    {formData.estacionamiento.tipo && (
                      <div className="mt-2">
                        <small className="text-muted d-block mb-1">Facilidad para estacionar:</small>
                        <div className="d-flex flex-wrap gap-2">
                          {[
                            { id: 'muy_facil', label: 'Muy facil' },
                            { id: 'facil', label: 'Facil' },
                            { id: 'medio', label: 'Medio' },
                            { id: 'dificil', label: 'Dificil' }
                          ].map(opcion => (
                            <button
                              type="button"
                              key={opcion.id}
                              className={`btn btn-sm ${formData.estacionamiento.facilidad === opcion.id ? 'btn-info' : 'btn-outline-secondary'}`}
                              onClick={() => handleChange('estacionamiento', {
                                ...formData.estacionamiento,
                                facilidad: opcion.id
                              })}
                              disabled={saving}
                            >
                              {opcion.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Zonas (solo proveedores) */}
              {esProveedor && (
                <div className="mb-3">
                  <label className="form-label">Zonas de servicio</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.zonas}
                    onChange={(e) => handleChange('zonas', e.target.value)}
                    disabled={saving}
                    placeholder="Separadas por coma: Polanco, Roma, Condesa"
                  />
                  <small className="text-muted">Colonias o zonas donde ofreces tus servicios</small>
                </div>
              )}

              {/* Metodos de pago */}
              <div className="mb-3">
                <label className="form-label">Metodos de pago</label>
                <div className="d-flex flex-wrap gap-2">
                  {metodosPagoOpciones.map(metodo => (
                    <button
                      type="button"
                      key={metodo}
                      className={`btn btn-sm ${formData.metodosPago.includes(metodo) ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => handleMetodoPagoToggle(metodo)}
                      disabled={saving}
                    >
                      {metodo.charAt(0).toUpperCase() + metodo.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Servicios */}
              {serviciosDisponibles.length > 0 && (
                <div className="mb-3">
                  <label className="form-label">Servicios que ofreces</label>
                  <div className="d-flex flex-wrap gap-2">
                    {serviciosDisponibles.map(servicio => (
                      <button
                        type="button"
                        key={servicio}
                        className={`btn btn-sm ${formData.servicios.includes(servicio) ? 'btn-success' : 'btn-outline-secondary'}`}
                        onClick={() => handleServicioToggle(servicio)}
                        disabled={saving}
                      >
                        {servicio}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Categoria (solo lectura) */}
              <div className="mb-4 p-3 bg-light rounded">
                <small className="text-muted d-block mb-1">Categoria</small>
                <span className="badge bg-secondary">{lugar?.categoria}</span>
                <small className="text-muted d-block mt-1">
                  La categoria no se puede modificar. Contacta a LemmonTags si necesitas cambiarla.
                </small>
              </div>

              {/* Boton guardar */}
              <div className="d-grid">
                <button
                  type="submit"
                  className="btn btn-primary btn-lg"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                      Guardando...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-lg me-2"></i>
                      Guardar cambios
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-4 pb-4">
          <small className="text-muted">
            Powered by <a href="https://lemmontags.com" className="text-decoration-none">LemmonTags</a>
          </small>
        </div>
      </div>
    </div>
  );
}
